import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BuyerService } from '../../services/buyer.service';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { PERMISSIONS } from '../../constants/permissions';
import BuyerFormDialog from './BuyerFormDialog';

const SEARCH_DEBOUNCE_MS = 300;
const COC_EXPIRING_WITHIN_DAYS = 30;

type CocStatus = 'expired' | 'expiring' | null;

/** Parses a `YYYY-MM-DD` string into a local midnight `Date` -- `new Date('YYYY-MM-DD')` parses as
 *  UTC, which shifts the date near midnight in Dhaka. Returns `null` for anything malformed. */
const parseLocalDate = (isoDate: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
    if (!match) return null;
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
};

/** CoC expiry status for the warning chip (spec 7.5): expired if `cocExpiresOn` is in the past,
 *  expiring if within the next 30 days. Computed once per row against a `today` hoisted out of the
 *  row loop, rather than re-parsed/re-compared per chip. */
const cocStatus = (buyer: Pick<IBuyer, 'cocExpiresOn'>, today: Date): CocStatus => {
    if (!buyer.cocExpiresOn) return null;
    const expiry = parseLocalDate(buyer.cocExpiresOn);
    if (!expiry) return null;
    if (expiry < today) return 'expired';
    const diffDays = Math.round((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= COC_EXPIRING_WITHIN_DAYS) return 'expiring';
    return null;
};

/** Buyer master list (garments-merchandising T8) — G20: MUI Table + TablePagination, never
 *  DataGrid, server-side paged through the Page<T> endpoint. Buyers are referenced master data:
 *  Activate/Deactivate only, no delete affordance. */
export function BuyerList() {
    const { hasPermission } = useAuth();
    const toast = useToast();
    const canManage = hasPermission(PERMISSIONS.BUYER_MANAGE);

    const [rows, setRows] = useState<IBuyer[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    const [formOpen, setFormOpen] = useState(false);
    const [editingBuyer, setEditingBuyer] = useState<IBuyer | null>(null);

    // Hoisted once per render rather than once per row -- `cocStatus` per row only needs a
    // local-midnight "today" to compare against.
    const todayForCoc = new Date();
    todayForCoc.setHours(0, 0, 0, 0);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await BuyerService.list(institutionId, page, size, search.trim() || undefined);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load buyers');
        } finally {
            setLoading(false);
        }
    }, [page, size, search, toast]);

    useEffect(() => {
        const handle = setTimeout(load, search ? SEARCH_DEBOUNCE_MS : 0);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size, search]);

    const openCreate = () => { setEditingBuyer(null); setFormOpen(true); };
    const openEdit = (buyer: IBuyer) => { setEditingBuyer(buyer); setFormOpen(true); };

    const handleToggleActive = async (buyer: IBuyer) => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const verb = buyer.active ? 'deactivate' : 'activate';
        if (!window.confirm(`Are you sure you want to ${verb} "${buyer.name}"?`)) return;
        try {
            const updated = buyer.active
                ? await BuyerService.deactivate(institutionId, buyer.id)
                : await BuyerService.activate(institutionId, buyer.id);
            setRows(prev => prev.map(b => (b.id === updated.id ? updated : b)));
            toast.showSuccess(buyer.active ? 'Buyer deactivated' : 'Buyer activated');
        } catch (e) {
            toast.showApiError(e, `Failed to ${verb} buyer`);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.buyers.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Buyers</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }} data-help-section="merchandising.buyers.filter">
                    <TextField
                        data-help="merchandising.buyers.search"
                        size="small"
                        label="Search"
                        placeholder="Code or name..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        sx={{ flex: '1 1 220px', maxWidth: 340 }}
                    />
                    {canManage && (
                        <Button data-help="merchandising.buyers.new" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                            New Buyer
                        </Button>
                    )}
                </Box>
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="merchandising.buyers.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.buyers.col.code">Code</TableCell>
                                <TableCell data-help="merchandising.buyers.col.name">Name</TableCell>
                                <TableCell data-help="merchandising.buyers.col.buyingHouse">Buying House</TableCell>
                                <TableCell data-help="merchandising.buyers.col.agent">Agent</TableCell>
                                <TableCell data-help="merchandising.buyers.col.country">Country</TableCell>
                                <TableCell data-help="merchandising.buyers.col.contact">Contact</TableCell>
                                <TableCell data-help="merchandising.buyers.col.email">Email</TableCell>
                                <TableCell data-help="merchandising.buyers.col.audit">Audit Standard</TableCell>
                                <TableCell data-help="merchandising.buyers.col.status">Status</TableCell>
                                {canManage && <TableCell align="right" data-help="merchandising.buyers.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => {
                                const buyerCocStatus = cocStatus(r, todayForCoc);
                                return (
                                <TableRow key={r.id} hover sx={{ opacity: r.active ? 1 : 0.55 }}>
                                    <TableCell>{r.code}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>{r.buyingHouse ?? '—'}</TableCell>
                                    <TableCell>{r.agentPartyName ?? '—'}</TableCell>
                                    <TableCell>{r.countryCode ?? '—'}</TableCell>
                                    <TableCell>{r.contactPerson ?? '—'}</TableCell>
                                    <TableCell>{r.email ?? '—'}</TableCell>
                                    <TableCell>{r.auditStandard ?? '—'}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            <Chip
                                                data-help="merchandising.buyers.status"
                                                label={r.active ? 'Active' : 'Inactive'}
                                                color={r.active ? 'success' : 'default'}
                                                size="small"
                                                variant={r.active ? 'filled' : 'outlined'}
                                            />
                                            {buyerCocStatus === 'expired' && (
                                                <Chip data-help="merchandising.buyers.coc" label="CoC expired" color="error" size="small" />
                                            )}
                                            {buyerCocStatus === 'expiring' && (
                                                <Chip data-help="merchandising.buyers.coc" label="CoC expiring" color="warning" size="small" />
                                            )}
                                        </Box>
                                    </TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <Button data-help="merchandising.buyers.edit" size="small" onClick={() => openEdit(r)}>Edit</Button>
                                            <Button
                                                size="small"
                                                color={r.active ? 'warning' : 'success'}
                                                data-help="merchandising.buyers.toggleActive" onClick={() => handleToggleActive(r)}
                                            >
                                                {r.active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                                );
                            })}
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={canManage ? 10 : 9} align="center">No buyers found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}

            <BuyerFormDialog
                open={formOpen}
                buyer={editingBuyer}
                onClose={() => setFormOpen(false)}
                onSaved={load}
            />
        </Box>
    );
}

export default BuyerList;
